import type { ColumnType } from 'antd/es/table';
import cn from 'classnames';
import { useMemo } from 'react';

import { sortByTime } from 'utils/antd/sorters';
import { formatAmount } from 'utils/formatters/formatAmount';
import { formatDecimalAmount } from 'utils/formatters/formatCurrency';
import { hasValue } from 'utils/hasValue';
import { padColumns } from 'utils/pad-columns';

import type { Movement } from 'api';

import { ClampedCell } from '@atoms/ClampedCell';
import { ColumnTitle } from '@atoms/ColumnTitle';
import { FormattedIdentifier } from '@atoms/FormattedIdentifier/FormattedIdentifier';
import { TransactionIconPlus, TransactionIconMinus } from '@atoms/Icon';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

export const useTransactionsColumns = (): ColumnType<Movement>[] => {
  const columns: ColumnType<Movement>[] = useMemo(
    () => [
      {
        dataIndex: 'amount',
        key: 'icon',
        width: 100,
        render: (amount: number) => (
          <div className="m-auto w-6">
            {amount < 0 ? (
              <TransactionIconMinus className="size-8" />
            ) : (
              <TransactionIconPlus className="size-8" />
            )}
          </div>
        )
      },
      {
        title: <ColumnTitle title="Date" />,
        dataIndex: 'date',
        width: 150,
        key: 'date',
        sorter: (a, b) => sortByTime(a.date, b.date),
        render: (date: string) => (
          <FormattedDateTime value={date} format="cet" />
        )
      },
      {
        title: <ColumnTitle title="ID/Reference" />,
        dataIndex: 'reference',
        key: 'idReference',
        width: 200,
        render: (reference: string, row: Movement) => (
          <ClampedCell className="text-balance uppercase">
            {row.order_id !== null && (
              <FormattedIdentifier
                value={`${row.order_id}`}
                className="text-xs"
              />
            )}
            <FormattedIdentifier value={reference} className="font-medium" />
          </ClampedCell>
        )
      },
      {
        title: <ColumnTitle title="Type" />,
        dataIndex: 'entry_type',
        key: 'entryType',
        render: (entryType: string) => {
          const value = entryType.replaceAll('_', ' ');

          return (
            <FormattedIdentifier
              value={value}
              className="text-base first-letter:capitalize"
            />
          );
        }
      },
      {
        title: <ColumnTitle title="Balance/Amount" />,
        dataIndex: 'running_balance',
        key: 'balanceAmount',
        align: 'right' as ColumnType<Movement>['align'],
        render: (runningBalance: string, row: Movement) => {
          if (!hasValue(row.amount)) {
            return;
          }
          const formattedAmount = formatDecimalAmount(Number(row.amount));
          const isPositive = Number(row.amount) >= 0;

          return (
            <>
              <div className="text-xs font-normal uppercase">
                {formatAmount({
                  amount: runningBalance,
                  currency: row.currency
                })}
              </div>
              <div
                className={cn(
                  'flex items-center justify-end text-base font-medium',
                  {
                    'text-content-positive': isPositive
                  }
                )}
              >
                <p>{formattedAmount}</p>
                <p>
                  &nbsp;
                  {row.currency}
                </p>
              </div>
            </>
          );
        }
      }
    ],
    []
  );

  return padColumns(columns);
};
